import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {WeaveStatsSummarySection} from '@wandb/weave/common/components/WeaveStatsSummarySection';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {TimeDelta} from '@wandb/weave/common/util/time';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components';
import {Icon} from '@wandb/weave/components/Icon';
import {ProvidersTab} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/OverviewPage/ProvidersTab';
import {WFDataModelAutoProvider} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useContext, useEffect} from 'react';
import {Link} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Dropdown, Image, Menu, Modal, Tab} from 'semantic-ui-react';

import modalImg from '../../assets/submit_benchmark_run.png';
import config, {envIsLocal} from '../../config';
import {useProjectMembersQuery, UserAccountType} from '../../generated/graphql';
import {ProjectData} from '../../state/graphql/projectPageQuery';
import {PremiumFeatureTooltip} from '../../util/accounts/pricing';
import {useAdminModeActive} from '../../util/admin';
import DOC_URLS from '../../util/doc_urls';
import {trackPageViewed} from '../../util/navigation';
import {
  AccessOption,
  getDisplayNameForProjectAccess,
  getIconForProjectAccess,
  getProjectAccess,
} from '../../util/permissions';
import {checkIsServiceAccount} from '../../util/serviceAccounts';
import {benchmarkProject, contactSalesPricing} from '../../util/urls';
import {ProjectRateLimits} from '../Admin/ProjectRateLimits';
import CodeBlock from '../CodeBlock';
import {ExtraProjectActions} from '../ExtraProjectActions';
import {useGetRoleOptions} from '../MembersTable/TeamMembersTable/useGetRoleOptions';
import {useIsProjectSpecificRolesEnabled} from '../Project/hooks';
import {ProjectMembersTable} from '../Project/ProjectMembersTable/ProjectMembersTable';
import {ProjectRoleMember} from '../Project/ProjectMembersTable/utils';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import * as S from './ProjectOverview.styles';
import {TAB_TITLES, useProjectOverviewTabs} from './useProjectOverviewTabs';
interface Example {
  title: string;
  description: React.ReactNode;
  getCode?: (entityName: string, projectName: string) => string;
}

const EXAMPLES: Example[] = [
  {
    title: 'Find the run path',
    description: (
      <S.CodeBlockDescription>
        To use the public API, you'll often need the run path which is{' '}
        <S.InlineCode>{'<entity>/<project>/<run_id>'}</S.InlineCode>. In the app
        UI, open a run page, then click on the Overview tab to see the run path.
      </S.CodeBlockDescription>
    ),
  },
  {
    title: 'Update config for an existing run',
    description: 'This example updates one of your configuration settings.',
    getCode: (entityName, projectName) => `import wandb
api = wandb.Api()

run = api.run("${entityName}/${projectName}/<run_id>")
run.config["key"] = updated_value
run.update()`,
  },
  {
    title: 'Export metrics from a single run to a CSV file',
    description:
      'This snippet finds all the metrics saved for a single run and saves them to a CSV file.',
    getCode: (entityName, projectName) => `import wandb
api = wandb.Api()

# run is specified by <entity>/<project>/<run_id>
run = api.run("${entityName}/${projectName}/<run_id>")

# save the metrics for the run to a csv file
metrics_dataframe = run.history()
metrics_dataframe.to_csv("metrics.csv")`,
  },
  {
    title: 'Read metrics for a run',
    description: (
      <S.CodeBlockDescription>
        Pull down the accuracy and timestamps for logged metric data. In this
        example, data was logged with{' '}
        <S.InlineCode>{'wandb.log({"accuracy": acc})'}</S.InlineCode> to a run
        with the run path{' '}
        <S.InlineCode>{'<entity>/<project>/<run_id>'}</S.InlineCode>.
      </S.CodeBlockDescription>
    ),
    getCode: (entityName, projectName) => `import wandb
api = wandb.Api()

run = api.run("${entityName}/${projectName}/<run_id>")
if run.state == "finished":
    for i, row in run.history().iterrows():
      print(row["_timestamp"], row["accuracy"])`,
  },
  {
    title: 'Get unsampled metric data',
    description:
      "When you pull data from history, by default it's sampled to 500 points. Get all the logged data points using run.scan_history(). Here's an example downloading all the loss data points logged in history.",
    getCode: (entityName, projectName) => `import wandb
api = wandb.Api()

run = api.run("${entityName}/${projectName}/<run_id>")
history = run.scan_history()
losses = [row["loss"] for row in history]`,
  },
  {
    title: 'Download the best model file from a sweep',
    description:
      'This snippet downloads the model file with the highest validation accuracy from a sweep with runs that saved model files to model.h5.',
    getCode: (entityName, projectName) => `import wandb
api = wandb.Api()

sweep = api.sweep("${entityName}/${projectName}/<sweep_id>")
runs = sorted(sweep.runs,
  key=lambda run: run.summary.get("val_acc", 0), reverse=True)
val_acc = runs[0].summary.get("val_acc", 0)
print(f"Best run {runs[0].name} with {val_acc}% validation accuracy")

runs[0].file("model.h5").download(replace=True)
print("Best model saved to model-best.h5")`,
  },
];

interface ProjectOverviewProps {
  project: ProjectData;
  hasWeaveData?: boolean;
  hasModelsData?: boolean;
}

const ProjectOverview: React.FC<ProjectOverviewProps> = ({
  project,
  hasWeaveData = false,
  hasModelsData = true,
}) => {
  // for page view tracking
  useEffect(() => {
    trackPageViewed('Project Overview');
  }, []);

  const isAdmin = useAdminModeActive();
  const {enableProjectSpecificRoles} = useIsProjectSpecificRolesEnabled();

  const projectAccess = getProjectAccess(
    project.access ?? AccessOption.Private
  );

  const projectAccessIsRestrictedOrPrivate =
    projectAccess === AccessOption.Private ||
    projectAccess === AccessOption.Restricted;

  const showUsersTab =
    enableProjectSpecificRoles &&
    project.entity.isTeam &&
    projectAccessIsRestrictedOrPrivate;

  // Determine if we should show the providers tab
  // Only show providers tab when there's weave data or when there's no models data
  const showProvidersTab = hasWeaveData || !hasModelsData;
  const isWeaveOnly = !hasModelsData && hasWeaveData;

  const {activeTabIndex, handleTabChange} = useProjectOverviewTabs(
    showUsersTab,
    showProvidersTab,
    project
  );

  const {data} = useProjectMembersQuery({
    variables: {
      entityName: project.entityName,
      projectName: project.name,
    },
    skip:
      projectAccess !== AccessOption.Restricted && !enableProjectSpecificRoles,
    fetchPolicy: 'cache-and-network',
  });

  const projectMembers = data?.project?.members ?? [];
  const nonOwnerprojectMembers =
    projectMembers.filter(m => m.id !== project.user?.id) ?? [];

  // selectedAccount and isEnterprise are copied from ServiceAccounts.tsx
  const {selectedAccount} = useContext(AccountSelectorContext);
  // technically you should have a valid license to use team project roles in onprem environments,
  // but since you can't create teams without a license anyway, checking the config is fine for now until we
  // get account selector in server.
  const isEnterprise =
    selectedAccount?.isEnterprise || config.ENVIRONMENT_IS_PRIVATE;

  return (
    <div className="project-overview">
      <div className="project-overview-top">
        <div className="name-header-wrapper">
          <h1 className="name-header">{project.name}</h1>
          <ExtraProjectActions project={project} isWeaveOnly={isWeaveOnly} />
        </div>
        {project.description && project.description !== '' && (
          <div className="description">{project.description}</div>
        )}
      </div>

      <WFDataModelAutoProvider>
        {(showUsersTab || showProvidersTab) && (
          <Tab
            panes={[
              {
                menuItem: TAB_TITLES[0],
                render: () => (
                  <ProjectDetails
                    project={project}
                    projectAccess={projectAccess}
                    isAdmin={isAdmin}
                    projectMembers={projectMembers}
                    enableProjectSpecificRoles={enableProjectSpecificRoles}
                    hasModelsData={hasModelsData}
                    hasWeaveData={hasWeaveData}
                  />
                ),
              },
              ...(showUsersTab
                ? [
                    {
                      menuItem: (
                        <Menu.Item key="users">
                          {TAB_TITLES[1]}
                          {!isEnterprise && (
                            <Tailwind>
                              <div className="-mt-4 !h-16">
                                <PremiumFeatureTooltip
                                  isPremiumFeatureDisabled={!isEnterprise}
                                />
                              </div>
                            </Tailwind>
                          )}
                        </Menu.Item>
                      ),
                      render: () => (
                        <ProjectUsers
                          project={project}
                          projectMembers={projectMembers}
                          projectAccess={projectAccess}
                          isEnterprise={isEnterprise}
                        />
                      ),
                    },
                  ]
                : []),
              ...(showProvidersTab
                ? [
                    {
                      menuItem: TAB_TITLES[2],
                      render: () => (
                        <ProvidersTab
                          entityName={project.entityName}
                          projectName={project.name}
                        />
                      ),
                    },
                  ]
                : []),
            ]}
            activeIndex={activeTabIndex}
            onTabChange={handleTabChange}
            menu={{
              secondary: true,
              pointing: true,
            }}
          />
        )}

        {!showUsersTab && !showProvidersTab && (
          <ProjectDetails
            project={project}
            projectAccess={projectAccess}
            isAdmin={isAdmin}
            projectMembers={nonOwnerprojectMembers}
            enableProjectSpecificRoles={enableProjectSpecificRoles}
            hasModelsData={hasModelsData}
            hasWeaveData={hasWeaveData}
          />
        )}
      </WFDataModelAutoProvider>
    </div>
  );
};

interface ProjectUsersProps {
  project: ProjectData;
  projectMembers: ProjectRoleMember[];
  projectAccess: AccessOption;
  isEnterprise: boolean;
}

const ProjectUsers: React.FC<ProjectUsersProps> = ({
  project,
  projectMembers,
  projectAccess,
  isEnterprise,
}) => {
  const {roleOptions} = useGetRoleOptions(project.entityName);

  if (!isEnterprise) {
    return (
      <div className="project-users">
        <p>
          Use role management for team projects is not available with your
          current subscription plan. To enable this feature, please upgrade.
        </p>

        <TargetBlank href={contactSalesPricing()}>
          <Button>Contact Sales</Button>
        </TargetBlank>
      </div>
    );
  }

  return (
    <div className="project-users">
      <ProjectMembersTable
        projectID={project.id}
        roleOptions={roleOptions}
        projectName={project.name}
        projectOwnerID={project.user?.id ?? ''}
        projectTeamName={project.entityName}
        projectMembers={projectMembers}
        projectAccess={projectAccess}
      />
    </div>
  );
};

interface ProjectDetailsProps {
  project: ProjectData;
  projectAccess: AccessOption;
  isAdmin: boolean;
  projectMembers: ProjectRoleMember[];
  enableProjectSpecificRoles: boolean;
  hasModelsData: boolean;
  hasWeaveData: boolean;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  project,
  projectAccess,
  isAdmin,
  projectMembers,
  enableProjectSpecificRoles,
  hasModelsData,
  hasWeaveData,
}) => {
  const isWeaveOnly = !hasModelsData && hasWeaveData;

  return (
    <div className="project-details">
      <div
        className="project-details-top"
        style={isWeaveOnly ? {borderBottom: '0px'} : {}}>
        {project.linkedBenchmark && (
          <div className="overview-item">
            <div className="overview-key">Benchmark</div>
            <div className="overview-value">
              <Dropdown
                className="dropdown-menu-icon-button benchmark-dropdown"
                simple
                icon={null}
                trigger={
                  <>
                    <LegacyWBIcon name="benchmark" />
                    {'Linked to ' + project.linkedBenchmark.name}
                    <LegacyWBIcon name="chevron-expanded" />
                  </>
                }>
                <Dropdown.Menu>
                  <Dropdown.Item>
                    <Modal trigger={<Dropdown.Item>Submit run</Dropdown.Item>}>
                      <Modal.Header icon="benchmark">
                        Submit to benchmark
                      </Modal.Header>
                      <Modal.Content>
                        <p>
                          To submit a run from the table, open the menu and
                          click <b>Submit to benchmark</b>.
                        </p>
                        <img
                          alt="Screenshot showing the submission dropdown is located in the runs table to the left of the name"
                          style={{
                            width: '100%',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                          }}
                          src={modalImg}
                        />
                      </Modal.Content>
                    </Modal>
                  </Dropdown.Item>
                  <Dropdown.Item
                    href={benchmarkProject(project.linkedBenchmark)}>
                    Visit benchmark
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        )}
        <div className="overview-item">
          <div className="overview-key">Project visibility</div>
          <div className="overview-value" data-test="privacy-value">
            <ProjectAccessItem
              access={projectAccess}
              isTeamProject={project.entity.isTeam}
            />
          </div>
        </div>
        {project.lastActive != null && (
          <div className="overview-item">
            <div className="overview-key">Last active</div>
            <div className="overview-value">
              {new Date(project.lastActive).toLocaleString()}
            </div>
          </div>
        )}
        {!enableProjectSpecificRoles && project.user !== null && (
          <div className="overview-item">
            <div className="overview-key">Owner</div>
            <div className="overview-value">
              <UserLinkItem user={project.user}></UserLinkItem>
            </div>
          </div>
        )}
        {projectAccess === AccessOption.Restricted &&
          projectMembers.length > 0 && (
            <div className="overview-item">
              <div className="overview-key">
                {enableProjectSpecificRoles ? 'Users' : 'Members'}
              </div>
              <div className="overview-value">
                {enableProjectSpecificRoles
                  ? projectMembers.length
                  : projectMembers.map(m => {
                      return (
                        <UserLinkItem
                          key={m.id}
                          user={{
                            username: m.username ?? '',
                            name: m.name ?? '',
                            photoUrl: m.photoUrl ?? null,
                            accountType: m.accountType as UserAccountType,
                          }}></UserLinkItem>
                      );
                    })}
              </div>
            </div>
          )}

        {!isWeaveOnly && (
          <>
            {projectAccess !== AccessOption.Restricted && (
              <div className="overview-item">
                <div className="overview-key">Contributors</div>
                <div className="overview-value">{`${project.totalUsers} user${
                  project.totalUsers === 1 ? '' : 's'
                }`}</div>
              </div>
            )}
            <div className="overview-item">
              <div className="overview-key">Total runs</div>
              <div className="overview-value">{project.totalRuns}</div>
            </div>
          </>
        )}
        {hasWeaveData && (
          <WeaveStatsSummarySection
            entity={project.entityName}
            project={project.name}
          />
        )}
        {!isWeaveOnly && (
          <>
            <div className="overview-item">
              <div className="overview-key">Total compute</div>
              <div className="overview-value">
                {new TimeDelta(project.computeHours).toSingleUnitString()}
              </div>
            </div>
            {!envIsLocal && isAdmin && (
              <div className="overview-item">
                <div className="overview-key">
                  Rate limits
                  <Tooltip
                    position="bottom center"
                    trigger={
                      <Icon
                        color="text-moon-400 flex self-center"
                        name="info"
                      />
                    }>
                    Set 0 for default, -1 to disable user. Can change at entity
                    level on settings page.
                  </Tooltip>
                </div>
                <div className="overview-value">
                  <ProjectRateLimits
                    projectName={project.name}
                    entityName={project.entityName}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {!isWeaveOnly && (
        <S.ExampleSection>
          <S.ExampleSectionHeader>
            <S.ExampleSectionTitle>Export & update data</S.ExampleSectionTitle>
            <S.ExampleSectionDescription>
              Use our Public API to export data or update data, such as editing
              the config of existing runs. Learn more in{' '}
              <TargetBlank href={DOC_URLS.publicApi}>the docs →</TargetBlank>
            </S.ExampleSectionDescription>
          </S.ExampleSectionHeader>
          {EXAMPLES.map((example: Example, i: number) => (
            <S.CodeBlockWrapper key={i}>
              <CodeBlock
                title={example.title}
                description={example.description}
                code={example.getCode?.(
                  project.entityName,
                  project.name
                )}></CodeBlock>
            </S.CodeBlockWrapper>
          ))}
        </S.ExampleSection>
      )}
    </div>
  );
};

interface UserLinkItemProps {
  user: {
    username: string;
    name: string;
    photoUrl: string | null;
    accountType: UserAccountType | null;
  };
}

const UserLinkItem: React.FC<UserLinkItemProps> = ({user}) => {
  const {username, photoUrl, name, accountType} = user;
  const content = (
    <div className="user-item">
      <Image
        src={photoUrl ?? '/default-profile-picture.png'}
        avatar
        onError={(i: any) => (i.target.style.display = 'none')}
      />
      <span>{name}</span>
    </div>
  );

  if (checkIsServiceAccount(accountType)) {
    return content;
  }
  return (
    <Link className="user-link" to={`/${username}`}>
      {content}
    </Link>
  );
};

interface ProjectAccessItemProps {
  access: AccessOption;
  isTeamProject: boolean;
}

const ProjectAccessItem: React.FC<ProjectAccessItemProps> = ({
  access,
  isTeamProject,
}) => {
  const iconName = getIconForProjectAccess(access, isTeamProject);
  return (
    <Tailwind>
      <div className="flex items-center">
        {iconName && (
          <Icon name={getIconForProjectAccess(access, isTeamProject)} />
        )}
        <span className="ml-6">
          {getDisplayNameForProjectAccess(access, isTeamProject)}
        </span>
      </div>
    </Tailwind>
  );
};

export default ProjectOverview;
